import { Suspense, lazy, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { RoutesUrls } from '~shared/lib/router';

import { BaseLayout } from '~pages/layouts';
import { CabinetNotFoundPage, NotFoundPage } from '~pages/shared/not-found';

import { Loader } from '~shared/ui';
import { useSetUser, useUser } from '~entities/shared/user';
import { useSetProfile } from '~entities/kindergarten/profile';
import { useSetSettings } from '~entities/shared/settings';

// import { ConfirmReference } from '~features/auth/confirm-reference';

// eslint-disable-next-line react/display-name

const loadable = (factory: any) => () => {
  const Component = lazy(factory);

  return (
    <Suspense
      fallback={
        <div className="h-[95vh] max-h-[95vh] overflow-hidden flex justify-center items-center">
          <Loader />
        </div>
      }
    >
      <Component />
    </Suspense>
  );
};

const cabinetLoadable = (factory: any) => () => {
  const Component = lazy(factory);

  return (
    <Suspense
      fallback={
        <div className="h-[80vh] flex justify-center items-center">
          <Loader />
        </div>
      }
    >
      <Component />
    </Suspense>
  );
};

const RegistrationPage = loadable(() =>
  import('~pages/registration').then((module) => ({
    default: module.RegistrationPage,
  }))
);

const OpenDataPage = loadable(() =>
  import('~pages/shared/openData').then((module) => ({
    default: module.OpenDataPage,
  }))
);

const MainPage = loadable(() =>
  import('~pages/site/main').then((module) => ({
    default: module.MainPage,
  }))
);

const KindergartensPage = loadable(() =>
  import('~pages/site/kindergartens').then((module) => ({
    default: module.KindergartensPage,
  }))
);

const ApplicationStatusPage = loadable(() =>
  import('~pages/site/ApplicationStatusPage').then((module) => ({
    default: module.ApplicationStatusPage,
  }))
);

const StatisticsPage = loadable(() =>
  import('~pages/site/StatisticsPage').then((module) => ({
    default: module.StatisticsPage,
  }))
);

const KindergartenDetailPage = loadable(() =>
  import('~pages/site/kindergartenDetail').then((module) => ({
    default: module.KindergartenDetailPage,
  }))
);

const FamilyRelationPage = loadable(() =>
  import('~pages/site/FamilyRelation').then((module) => ({
    default: module.FamilyRelationPage,
  }))
);

const LoginPage = loadable(() =>
  import('~pages/shared/login').then((module) => ({
    default: module.LoginPage,
  }))
);

const MobileAuthPage = loadable(() =>
  import('~pages/shared/mobile-auth').then((module) => ({
    default: module.MobileAuthPage,
  }))
);

const EsiPage = loadable(() =>
  import('~pages/shared/esi').then((module) => ({
    default: module.EsiPage,
  }))
);

const EKyzmatPage = loadable(() =>
  import('~pages/shared/ekyzmat').then((module) => ({
    default: module.EKyzmatPage,
  }))
);

const MugalimPage = loadable(() =>
  import('~pages/shared/mugalim').then((module) => ({
    default: module.MugalimPage,
  }))
);

const LogoutPage = loadable(() =>
  import('~pages/shared/logout').then((module) => ({
    default: module.LogoutPage,
  }))
);

const RecoveryPage = loadable(() =>
  import('~pages/shared/recovery-password').then((module) => ({
    default: module.RecoveryPasswordPage,
  }))
);

const InstructionsReferencePage = loadable(() =>
  import('~pages/shared/instructions-reference').then((module) => ({
    default: module.InstructionsReferencePage,
  }))
);

const ConfirmReferencePage = loadable(() =>
  import('~pages/shared/confirm-reference').then((module) => ({
    default: module.ConfirmReferencePage,
  }))
);

const HomePage = cabinetLoadable(() =>
  import('~pages/kindergarten/home').then((module) => ({ default: module.HomePage }))
);

const ProfilePage = cabinetLoadable(() =>
  import('~pages/kindergarten/profile').then((module) => ({ default: module.ProfilePage }))
);

const MinistriesPage = cabinetLoadable(() =>
  import('~pages/kindergarten/ministries').then((module) => ({ default: module.MinistriesPage }))
);

const OrganizationPage = cabinetLoadable(() =>
  import('~pages/kindergarten/organization').then((module) => ({
    default: module.OrganizationPage,
  }))
);

const KindergartenVaucherPage = cabinetLoadable(() =>
  import('~pages/kindergarten/voucher').then((module) => ({
    default: module.VaucherPage,
  }))
);

// const MonVoucherAgreementsPage = cabinetLoadable(() =>
//   import('~pages/admin/voucher').then((module) => ({
//     default: module.VoucherAgreementsPage,
//   }))
// );

const StaffPage = cabinetLoadable(() =>
  import('~pages/kindergarten/staff').then((module) => ({ default: module.StaffPage }))
);

const KidsPage = cabinetLoadable(() =>
  import('~pages/kindergarten/kids').then((module) => ({ default: module.KidsPage }))
);

const ApplicationsPage = cabinetLoadable(() =>
  import('~pages/kindergarten/applications').then((module) => ({
    default: module.ApplicationsPage,
  }))
);

const ApplicationDetailPage = cabinetLoadable(() =>
  import('~pages/kindergarten/applications').then((module) => ({
    default: module.ApplicationDetailPage,
  }))
);

const GroupPage = cabinetLoadable(() =>
  import('~pages/kindergarten/group').then((module) => ({ default: module.GroupPage }))
);

const DepartmentsPage = cabinetLoadable(() =>
  import('~pages/kindergarten/departments').then((module) => ({ default: module.DepartmentsPage }))
);

const KidsAttenDancePage = cabinetLoadable(() =>
  import('~pages/kindergarten/attendance').then((module) => ({
    default: module.KidsAttendancePage,
  }))
);

const TimesheetPage = cabinetLoadable(() =>
  import('~pages/kindergarten/timesheet').then((module) => ({
    default: module.TimesheetPage,
  }))
);

const TrainingPage = cabinetLoadable(() =>
  import('~pages/kindergarten/training').then((module) => ({ default: module.TrainingPage }))
);

const HelpPage = cabinetLoadable(() =>
  import('~pages/kindergarten/help').then((module) => ({ default: module.HelpPage }))
);

const PrivacyPolicyPage = cabinetLoadable(() =>
  import('~pages/kindergarten/privacyPolicy').then((module) => ({
    default: module.PrivacyPolicyPage,
  }))
);

const DocumentsPage = cabinetLoadable(() =>
  import('~pages/kindergarten/documents').then((module) => ({ default: module.DocumentsPage }))
);

const EmployeePage = cabinetLoadable(() =>
  import('~pages/admin/employee').then((module) => ({ default: module.EmployeePage }))
);

const RegistryPage = cabinetLoadable(() =>
  import('~pages/admin/registry').then((module) => ({ default: module.RegistryPage }))
);

const ResetPasswordPage = cabinetLoadable(() =>
  import('~pages/shared/reset-password').then((module) => ({ default: module.ResetPasswordPage }))
);

const AdminRegistryPage = cabinetLoadable(() =>
  import('~pages/admin/adminRegistry').then((module) => ({ default: module.AdminRegistryPage }))
);

const MonFinancePage = cabinetLoadable(() =>
  import('~pages/admin/voucher').then((module) => ({ default: module.MonFinancePage }))
);

const MonVoucherPage = cabinetLoadable(() =>
  import('~pages/admin/voucher').then((module) => ({ default: module.MonVoucherPage }))
);

const MonHelpPage = cabinetLoadable(() =>
  import('~pages/admin/help').then((module) => ({ default: module.HelpPage }))
);

const ReportsPage = cabinetLoadable(() =>
  import('~pages/admin/reports').then((module) => ({ default: module.ReportsPage }))
);

const AymakFinanceFoodPage = cabinetLoadable(() =>
  import('~pages/mtu/AymakFinanceFoodPage').then((module) => ({
    default: module.AymakFinanceFoodPage,
  }))
);

const AymakFinanceOrganizationPage = cabinetLoadable(() =>
  import('~pages/mtu/AymakFinanceOrganizationPage').then((module) => ({
    default: module.AymakFinanceOrganizationPage,
  }))
);

const AymakFinanceChildrenVouchersPage = cabinetLoadable(() =>
  import('~pages/mtu/AymakFinanceChildrenVouchersPage').then((module) => ({
    default: module.AymakFinanceChildrenVouchersPage,
  }))
);

const HelpVouchersPage = cabinetLoadable(() =>
  import('~pages/mtu/Help').then((module) => ({
    default: module.HelpPage,
  }))
);

const ControlPage = cabinetLoadable(() =>
  import('~pages/admin/control').then((module) => ({ default: module.ControlPage }))
);

const AdminRegistryDistrictPage = cabinetLoadable(() =>
  import('~pages/admin/adminRegistryDistrict').then((module) => ({
    default: module.AdminRegistryDistrictPage,
  }))
);

const MonDocumentsPage = cabinetLoadable(() =>
  import('~pages/admin/documents').then((module) => ({
    default: module.DocumentsPage,
  }))
);

const InstructionsPage = cabinetLoadable(() =>
  import('~pages/shared/instructions').then((module) => ({
    default: module.InstructionsPage,
  }))
);

const AdminInstructionsPage = cabinetLoadable(() =>
  import('~pages/admin/instructions').then((module) => ({
    default: module.AdminInstructionsPage,
  }))
);

const ParentHomePage = cabinetLoadable(() =>
  import('~pages/parent/home').then((module) => ({
    default: module.ParentHomePage,
  }))
);

const ParentPersonalPage = cabinetLoadable(() =>
  import('~pages/parent/personal').then((module) => ({
    default: module.ParentPersonalPage,
  }))
);

const ParentEducationPage = cabinetLoadable(() =>
  import('~pages/parent/education').then((module) => ({
    default: module.ParentEducationPage,
  }))
);

const ParentVoucherPage = cabinetLoadable(() =>
  import('~pages/parent/voucher').then((module) => ({
    default: module.ParentVoucherPage,
  }))
);

const ParentPaymentPage = cabinetLoadable(() =>
  import('~pages/parent/payment').then((module) => ({
    default: module.ParentPaymentPage,
  }))
);

const ParentHelpPage = cabinetLoadable(() =>
  import('~pages/parent/help').then((module) => ({
    default: module.HelpPage,
  }))
);

const MembersPage = cabinetLoadable(() =>
  import('~pages/parent/members').then((module) => ({
    default: module.MembersPage,
  }))
);

const NotificationsPage = cabinetLoadable(() =>
  import('~pages/parent/notifications').then((module) => ({
    default: module.NotificationsPage,
  }))
);

const ParentChildrensPage = cabinetLoadable(() =>
  import('~pages/parent/childrens').then((module) => ({
    default: module.ParentChildrensPage,
  }))
);

const ParentAttendancePage = cabinetLoadable(() =>
  import('~pages/parent/attendance').then((module) => ({
    default: module.ParentAttendancePage,
  }))
);

const GovApplicationPage = cabinetLoadable(() =>
  import('~pages/parent/application/gov').then((module) => ({
    default: module.GovApplicationPage,
  }))
);

const PrivateApplicationPage = cabinetLoadable(() =>
  import('~pages/parent/application/private').then((module) => ({
    default: module.PrivateApplicationPage,
  }))
);

const ShortTermApplicationPage = cabinetLoadable(() =>
  import('~pages/parent/application/short-term').then((module) => ({
    default: module.ShortTermApplicationPage,
  }))
);

const SpecialApplicationPage = cabinetLoadable(() =>
  import('~pages/parent/application/special').then((module) => ({
    default: module.SpecialApplicationPage,
  }))
);

const DepartmentApplicationPage = cabinetLoadable(() =>
  import('~pages/parent/application/department').then((module) => ({
    default: module.DepartmentApplicationPage,
  }))
);

const ParentPage = cabinetLoadable(() =>
  import('~pages/site/parent').then((module) => ({
    default: module.ParentPage,
  }))
);

const ParentRegistrationPage = cabinetLoadable(() =>
  import('~pages/site/ParentRegistrationPage').then((module) => ({
    default: module.ParentRegistrationPage,
  }))
);

const MonDocumentsSitePage = cabinetLoadable(() =>
  import('~pages/site/Documents').then((module) => ({
    default: module.DocumentsPage,
  }))
);

const VoucherPage = cabinetLoadable(() =>
  import('~pages/site/VoucherPage').then((module) => ({
    default: module.VoucherPage,
  }))
);

const VoucherRegistrationPage = cabinetLoadable(() =>
  import('~pages/site/RegistrationPage').then((module) => ({
    default: module.RegistrationPage,
  }))
);

const VoucherFaqPage = cabinetLoadable(() =>
  import('~pages/site/VoucherFaqPage').then((module) => ({
    default: module.VoucherFaqPage,
  }))
);

const RequiredDocumentsPage = cabinetLoadable(() =>
  import('~pages/site/RequiredDocumentsPage').then((module) => ({
    default: module.RequiredDocumentsPage,
  }))
);

const SiteFaqPage = cabinetLoadable(() =>
  import('~pages/site/FaqPage').then((module) => ({
    default: module.FaqPage,
  }))
);

const VoucherAdminPage = cabinetLoadable(() =>
  import('~pages/mtu/VoucherAdminPage').then((module) => ({
    default: module.VoucherAdminPage,
  }))
);

// const VoucherAymakAgreementPage = cabinetLoadable(() =>
//   import('~pages/mtu/VoucherAymakAgreementPage').then((module) => ({
//     default: module.VoucherAymakAgreementPage,
//   }))
// );

const VoucherApplicationPage = cabinetLoadable(() =>
  import('~pages/mtu/voucherApplication/list').then((module) => ({
    default: module.VoucherApplicationPage,
  }))
);

const VoucherApplicationDetailPage = cabinetLoadable(() =>
  import('~pages/mtu/voucherApplication/detail').then((module) => ({
    default: module.VoucherApplicationDetailPage,
  }))
);

const CommissionPage = cabinetLoadable(() =>
  import('~pages/mtu/CommissionPage').then((module) => ({
    default: module.CommissionPage,
  }))
);

const AymakFoodPage = cabinetLoadable(() =>
  import('~pages/mtu/AymakFoodPage').then((module) => ({
    default: module.AymakFoodPage,
  }))
);

const RegistryInfoPage = cabinetLoadable(() =>
  import('~pages/admin/registryInfo').then((module) => ({ default: module.RegistryInfoPage }))
);

const BishkekVoucherPage = cabinetLoadable(() =>
  import('~pages/bishkek/voucher').then((module) => ({ default: module.BishkekVoucherPage }))
);

const BishkekHelpPage = cabinetLoadable(() =>
  import('~pages/bishkek/help').then((module) => ({ default: module.HelpPage }))
);

const AdminOrganizationPage = cabinetLoadable(() =>
  import('~pages/admin/mon/admin/organization').then((module) => ({
    default: module.OrganizationPage,
  }))
);

const AdminOrganizationAddPage = cabinetLoadable(() =>
  import('~pages/admin/mon/admin/organization-add').then((module) => ({
    default: module.OrganizationAddPage,
  }))
);

const AdminPasswordRecoveryPage = cabinetLoadable(() =>
  import('~pages/admin/mon/admin/passwordRecovery').then((module) => ({
    default: module.PasswordRecoveryPage,
  }))
);

const AdminChildApplicationSearchPage = cabinetLoadable(() =>
  import('~pages/admin/mon/admin/childApplicationSearch').then((module) => ({
    default: module.ChildApplicationSearchPage,
  }))
);

const AdminAcceleratorPage = cabinetLoadable(() =>
  import('~pages/admin/mon/admin/accelerator').then((module) => ({
    default: module.AcceleratorPage,
  }))
);

const AdminOrganizationDetailPage = cabinetLoadable(() =>
  import('~pages/admin/organizationDetail').then((module) => ({
    default: module.OrganizationDetailPage,
  }))
);

const SettingsPage = cabinetLoadable(() =>
  import('~pages/shared/settings').then((module) => ({ default: module.SettingsPage }))
);

const FeedbackPage = cabinetLoadable(() =>
  import('~pages/shared/feedback').then((module) => ({ default: module.FeedbackPage }))
);

interface RequireAuthProps {
  children: JSX.Element;
  loginPath: string;
}

const RequireAuth: React.FunctionComponent<RequireAuthProps> = ({ children, loginPath }) => {
  const user = useUser();

  const isAuth = () => {
    if (user) {
      return true;
    }

    return false;
  };

  const location = useLocation();

  if (!isAuth()) {
    // Redirect them to the /login page, but save the current location they were
    // trying to go to when they were redirected. This allows us to send them
    // along to that page after they login, which is a nicer user experience
    // than dropping them off on the home page.
    return <Navigate to={loginPath} state={{ from: location }} replace />;
  }

  return children;
};

const createProtectedElement = (component: JSX.Element) => (
  <RequireAuth loginPath={RoutesUrls.login}>{component}</RequireAuth>
);

export const Router = () => {
  const location = useLocation();
  const user = useUser();
  const setUser = useSetUser();
  const setProfile = useSetProfile();
  const setSettings = useSetSettings();

  useEffect(() => {
    if (!user) {
      setUser({ authState: null });
    }
  }, []);

  useEffect(() => {
    if (user && user?.role?.includes('accelerator')) {
      setProfile();
    }

    setSettings();
  }, [user]);

  return (
    <Routes location={location}>
      <Route path="/" element={<Navigate replace to={RoutesUrls.parent} />} />
      <Route path={RoutesUrls.doo} element={<MainPage />} />
      <Route path={RoutesUrls.parent} element={<ParentPage />} />
      <Route path={RoutesUrls.parentRegistration} element={<ParentRegistrationPage />} />
      <Route path={RoutesUrls.kindergartens} element={<KindergartensPage />} />
      <Route path={RoutesUrls.kindergartenDetail} element={<KindergartenDetailPage />} />
      <Route path={RoutesUrls.applicationStatus} element={<ApplicationStatusPage />} />
      <Route path={RoutesUrls.statistics} element={<StatisticsPage />} />
      <Route path={RoutesUrls.monDocumentsSitePage} element={<MonDocumentsSitePage />} />
      <Route path={RoutesUrls.voucher} element={<VoucherPage />} />
      <Route path={RoutesUrls.voucherRegistration} element={<VoucherRegistrationPage />} />
      <Route path={RoutesUrls.voucherFaq} element={<VoucherFaqPage />} />
      <Route path={RoutesUrls.siteFaq} element={<SiteFaqPage />} />
      <Route path={RoutesUrls.familyRelation} element={<FamilyRelationPage />} />
      <Route path={RoutesUrls.requiredDocuments} element={<RequiredDocumentsPage />} />

      <Route path={RoutesUrls.registration} element={<RegistrationPage />} />
      <Route path={RoutesUrls.openData} element={<OpenDataPage />} />
      <Route path={RoutesUrls.login} element={<LoginPage />} />
      <Route path={RoutesUrls.mobileAuth} element={<MobileAuthPage />} />
      <Route path={RoutesUrls.authCallback} element={<MobileAuthPage />} />
      <Route path={RoutesUrls.esi} element={<EsiPage />} />
      <Route path={RoutesUrls.ekyzmat} element={<EKyzmatPage />} />
      <Route path={RoutesUrls.mugalim} element={<MugalimPage />} />
      <Route path={RoutesUrls.recovery} element={<RecoveryPage />} />
      <Route path={RoutesUrls.resetPassword} element={<ResetPasswordPage />} />
      <Route path={RoutesUrls.instructionsReference} element={<InstructionsReferencePage />} />
      <Route path={RoutesUrls.confirmReference} element={<ConfirmReferencePage />} />

      <Route path={RoutesUrls.root} element={createProtectedElement(<BaseLayout />)}>
        <Route path={RoutesUrls.home} element={<HomePage />} />
        <Route path={RoutesUrls.personal} element={<ProfilePage />} />
        <Route path={RoutesUrls.ministries} element={<MinistriesPage />} />
        <Route path={RoutesUrls.organization} element={<OrganizationPage />} />
        <Route path={RoutesUrls.kindergartenVaucher} element={<KindergartenVaucherPage />} />
        <Route path={RoutesUrls.staff} element={<StaffPage />} />
        <Route path={RoutesUrls.monDocuments} element={<MonDocumentsPage />} />
        <Route path={RoutesUrls.monFinanceVoucher} element={<MonFinancePage />} />
        <Route path={RoutesUrls.monVoucher} element={<MonVoucherPage />} />
        <Route path={RoutesUrls.monHelp} element={<MonHelpPage />} />
        <Route path={RoutesUrls.kids} element={<KidsPage />} />
        <Route path={RoutesUrls.applications} element={<ApplicationsPage />} />
        <Route path={RoutesUrls.applicationDetail} element={<ApplicationDetailPage />} />
        <Route path={RoutesUrls.group} element={<GroupPage />} />
        <Route path={RoutesUrls.departments} element={<DepartmentsPage />} />
        <Route path={RoutesUrls.attendance} element={<KidsAttenDancePage />} />
        <Route path={RoutesUrls.attendanceReport} element={<TimesheetPage />} />
        <Route path={RoutesUrls.documents} element={<DocumentsPage />} />
        <Route path={RoutesUrls.training} element={<TrainingPage />} />
        <Route path={RoutesUrls.employee} element={<EmployeePage />} />
        <Route path={RoutesUrls.registry} element={<RegistryPage />} />
        <Route path={RoutesUrls.adminRegistry} element={<AdminRegistryPage />} />
        <Route path={RoutesUrls.adminRegistryDistrict} element={<AdminRegistryDistrictPage />} />
        <Route path={RoutesUrls.adminOrganization} element={<AdminOrganizationPage />} />
        <Route path={RoutesUrls.adminOrganizationAdd} element={<AdminOrganizationAddPage />} />
        <Route
          path={RoutesUrls.adminParentPasswordRecovery}
          element={<AdminPasswordRecoveryPage />}
        />
        <Route path={RoutesUrls.adminAccelerator} element={<AdminAcceleratorPage />} />
        <Route
          path={RoutesUrls.adminChildApplicationSearch}
          element={<AdminChildApplicationSearchPage />}
        />
        <Route
          path={RoutesUrls.adminOrganizationDetail}
          element={<AdminOrganizationDetailPage />}
        />
        <Route path={RoutesUrls.registry} element={<RegistryPage />} />
        <Route path={RoutesUrls.registryInfo} element={<RegistryInfoPage />} />
        <Route path={RoutesUrls.reports} element={<ReportsPage />} />
        <Route path={RoutesUrls.aymakFinanceFood} element={<AymakFinanceFoodPage />} />
        <Route
          path={RoutesUrls.aymakFinanceOrganization}
          element={<AymakFinanceOrganizationPage />}
        />{' '}
        <Route
          path={RoutesUrls.aymakFinanceChildrenVouchers}
          element={<AymakFinanceChildrenVouchersPage />}
        />
        <Route path={RoutesUrls.control} element={<ControlPage />} />
        {/*<Route path={RoutesUrls.voucherAgreements} element={<MonVoucherAgreementsPage />} />*/}
        <Route path={RoutesUrls.help} element={<HelpPage />} />
        <Route path={RoutesUrls.helpVoucher} element={<HelpVouchersPage />} />
        <Route path={RoutesUrls.logout} element={<LogoutPage />} />
        <Route path={RoutesUrls.privacyPolicy} element={<PrivacyPolicyPage />} />
        <Route path={RoutesUrls.settings} element={<SettingsPage />} />
        <Route path={RoutesUrls.feedback} element={<FeedbackPage />} />
        <Route path={RoutesUrls.instructions} element={<InstructionsPage />} />
        <Route path={RoutesUrls.adminInstructions} element={<AdminInstructionsPage />} />
        <Route path={RoutesUrls.notFound} element={<NotFoundPage />} />
        <Route path={RoutesUrls.cabinet + '/*'} element={<CabinetNotFoundPage />} />
        <Route path={RoutesUrls.parentCabinetHome} element={<ParentHomePage />} />
        <Route path={RoutesUrls.parentCabinetMembers} element={<MembersPage />} />
        <Route path={RoutesUrls.parentCabinetNotifications} element={<NotificationsPage />} />
        <Route path={RoutesUrls.parentCabinetVoucher} element={<ParentVoucherPage />} />
        <Route path={RoutesUrls.parentCabinetPayment} element={<ParentPaymentPage />} />
        <Route path={RoutesUrls.parentCabinetPersonal} element={<ParentPersonalPage />} />
        <Route path={RoutesUrls.parentCabinetEducation} element={<ParentEducationPage />} />
        <Route path={RoutesUrls.parentCabinetChildrenList} element={<ParentChildrensPage />} />
        <Route path={RoutesUrls.parentCabinetAttendance} element={<ParentAttendancePage />} />
        <Route path={RoutesUrls.parentCabinet + '/*'} element={<CabinetNotFoundPage />} />
        {/* Маршруты для заявок родителей */}
        <Route path={RoutesUrls.parentApplicationGov} element={<GovApplicationPage />} />
        <Route path={RoutesUrls.parentApplicationPrivate} element={<PrivateApplicationPage />} />
        <Route
          path={RoutesUrls.parentApplicationShortTerm}
          element={<ShortTermApplicationPage />}
        />
        <Route path={RoutesUrls.parentApplicationSpecial} element={<SpecialApplicationPage />} />
        <Route
          path={RoutesUrls.parentApplicationDepartment}
          element={<DepartmentApplicationPage />}
        />
        <Route path={RoutesUrls.parentHelp} element={<ParentHelpPage />} />
        <Route path={RoutesUrls.voucherApplication} element={<VoucherApplicationPage />} />
        <Route path={RoutesUrls.voucherAdmin} element={<VoucherAdminPage />} />
        <Route
          path={RoutesUrls.voucherApplicationDetail}
          element={<VoucherApplicationDetailPage />}
        />
        <Route path={RoutesUrls.voucherCommission} element={<CommissionPage />} />
        {/* <Route path={RoutesUrls.voucherAymakAgreement} element={<VoucherAymakAgreementPage />} /> */}
        <Route path={RoutesUrls.voucherAymakFood} element={<AymakFoodPage />} />
        <Route path={RoutesUrls.bishkekVoucher} element={<BishkekVoucherPage />} />
        <Route path={RoutesUrls.bishkekHelp} element={<BishkekHelpPage />} />
      </Route>
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};
